import MetaOverride from './MetaOverride';
import { copyObject } from '../libs';
import MetaField from "./MetaField";
import Configuration from "./Configuration";

const INVALID_OVERRIDE_ATTRIBUTE_MAP = {
  options: true,
};

class MetaOverrideSet {
  constructor(args = {}) {
    this._overrides = [];
    this._name = 'metaOverrideSet';
    this._immutable = false;
    if (args.base) {
      this._overrides = copyObject(args.base._overrides);
    }
  }

  * [Symbol.iterator]() {
    for (const v of this._overrides) {
      yield v;
    }
  }

  list() {
    return this._overrides;
  }

  name(s) {
    this._name = s;
    return this;
  }

  override(filterObj, override, args) {
    if (this._immutable) {
      const copy = new MetaOverrideSet({ base: this })
      return copy.override(filterObj, override, args)
    }
    this._overrides = [ ...this._overrides, new MetaOverride(filterObj, override, args) ];

    return this;
  }

  showRequiredFields(args) {
    return this.override({
      required: true,
    }, { hidden: false, display: true }, args);
  }

  hideNonRequiredFields(args) {
    return this.override({
      display: display => !display,
      required: false,
    }, { hidden: true }, args);
  }

  nameOverride(names, override, args) {
    const overriddenNames = (Array.isArray(names)) ? (name) => names.includes(name) : names;

    return this.override({ name: overriddenNames }, override, args);
  }

  typeOverride(type, override, args) {
    return this.override({ type }, override, args);
  }

  overrideAll(override, args) {
    return this.override({}, override, args);
  }

  clearOverrides() {
    if (this._immutable) {
      throw new Error("Can't clear overrides after calling done(), which seals them")
    }
    this._overrides = [];

    return this;
  }

  // This function is needed by facet generate. Please don't remove
  done() {
    this._immutable = true;
    return this;
  }

  applyOverrides(metaField) {
    const assertValidOverride = (override) => {
      for (const key in override.value()) {
        if (INVALID_OVERRIDE_ATTRIBUTE_MAP[key]) {
          throw new Error(`Invalid override of key: ${key}. You can't do this when overriding as a mask.`);
        }
      }
      return true;
    };

    const newField = new MetaField();
    newField.shallowCopy(metaField);

    const allOverrides = [...Configuration.listGlobalMetaOverrides(), ...this._overrides];

    // Accumulate all possible overrides and sort by priority
    const unprioritizedOverrides = allOverrides.filter(v => v.ordinal() === 0);
    const prioritizedOverrides = allOverrides.filter(v => v.ordinal() > 0).sort((a, b) => a.ordinal() - b.ordinal());
    const sortedOverrides = [
      ...unprioritizedOverrides,
      ...prioritizedOverrides,
    ];

    const finalizedField = sortedOverrides
      .reduce((accumulatedField, currentOverride) => {
        // If not a match then continue without amending field
        if (!currentOverride.matches(accumulatedField)) {
          return accumulatedField;
        }

        // Ensure override isn't trying to adjust options
        assertValidOverride(currentOverride);

        return currentOverride.apply(accumulatedField, { shouldSkipMatch: true });
      }, newField);

    return finalizedField;
  }
}

export default MetaOverrideSet;
